import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface HelpContextType {
  showHelp: boolean;
  toggleHelp: () => void;
  isHelpEnabled: (helpId: string) => boolean;
  disableHelp: (helpId: string) => void;
  enableHelp: (helpId: string) => void;
  resetAllHelp: () => void;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

interface HelpProviderProps {
  children: ReactNode;
}

const HELP_STORAGE_KEY = 'veresiye_help_settings';

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const [showHelp, setShowHelp] = useState(true);
  const [disabledHelp, setDisabledHelp] = useState<Set<string>>(new Set());

  // Load help settings from localStorage on mount
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem(HELP_STORAGE_KEY);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setShowHelp(settings.showHelp ?? true);
        setDisabledHelp(new Set(settings.disabledHelp || []));
      }
    } catch {
      // Corrupt local preferences fall back to the default help settings.
    }
  }, []);

  // Save help settings to localStorage whenever they change
  useEffect(() => {
    try {
      const settings = {
        showHelp,
        disabledHelp: Array.from(disabledHelp)
      };
      localStorage.setItem(HELP_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Storage can be unavailable in private browsing; in-memory settings remain usable.
    }
  }, [showHelp, disabledHelp]);

  const toggleHelp = () => {
    setShowHelp(prev => !prev);
  };

  const isHelpEnabled = (helpId: string): boolean => {
    return showHelp && !disabledHelp.has(helpId);
  };

  const disableHelp = (helpId: string) => {
    setDisabledHelp(prev => new Set(Array.from(prev).concat(helpId)));
  };

  const enableHelp = (helpId: string) => {
    setDisabledHelp(prev => {
      const newSet = new Set(prev);
      newSet.delete(helpId);
      return newSet;
    });
  };

  const resetAllHelp = () => {
    setShowHelp(true);
    setDisabledHelp(new Set());
  };

  const value: HelpContextType = {
    showHelp,
    toggleHelp,
    isHelpEnabled,
    disableHelp,
    enableHelp,
    resetAllHelp
  };

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = (): HelpContextType => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};

export default HelpContext;
